"use client";

import { useState, useSyncExternalStore } from "react";

// A row of cards the user can rearrange by dragging. Each card gets a small
// grip in its top-right corner (shown on hover); drag it onto another card to
// put the dragged one in that spot. The order is remembered in this browser.
// Uses the browser's built-in drag and drop, so it works with a mouse (not
// touch screens).

export type SortableItem = { id: string; node: React.ReactNode };

// The saved order lives in localStorage. useSyncExternalStore reads it after
// hydration (the server render and first paint use the default order), and
// re-reads whenever a row saves a new one.
const listeners = new Set<() => void>();
const subscribe = (callback: () => void) => {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
};

function readRaw(storageKey: string): string | null {
  try {
    return localStorage.getItem(storageKey);
  } catch {
    return null; // storage blocked: use the default order
  }
}

function resolveOrder(raw: string | null, ids: string[]): string[] {
  try {
    const saved = JSON.parse(raw ?? "null");
    if (Array.isArray(saved)) {
      // Keep only cards that still exist, and add any new ones at the end.
      const known = saved.filter((id: unknown): id is string => typeof id === "string" && ids.includes(id));
      return [...known, ...ids.filter((id) => !known.includes(id))];
    }
  } catch {
    // unreadable: fall through to the default
  }
  return ids;
}

export function SortableRow({
  storageKey,
  items,
  className,
}: {
  storageKey: string;
  items: SortableItem[];
  className?: string;
}) {
  const ids = items.map((i) => i.id);
  const raw = useSyncExternalStore(subscribe, () => readRaw(storageKey), () => null);
  const order = resolveOrder(raw, ids);
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  // Only the grip starts a drag, so buttons and sliders inside a card keep working.
  const [armed, setArmed] = useState<string | null>(null);

  function save(next: string[]) {
    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      // not remembered across visits
    }
    listeners.forEach((l) => l());
  }

  function drop(targetId: string) {
    if (dragging && dragging !== targetId) {
      const without = order.filter((id) => id !== dragging);
      without.splice(without.indexOf(targetId), 0, dragging);
      save(without);
    }
    setDragging(null);
    setOver(null);
    setArmed(null);
  }

  const byId = new Map(items.map((i) => [i.id, i]));
  const ordered = order.map((id) => byId.get(id)).filter((i): i is SortableItem => !!i);

  return (
    <div className={className}>
      {ordered.map((item) => (
        <div
          key={item.id}
          draggable={armed === item.id}
          onDragStart={(e) => {
            setDragging(item.id);
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", item.id);
          }}
          onDragOver={(e) => {
            if (!dragging) return;
            e.preventDefault();
            setOver(item.id);
          }}
          onDragLeave={() => setOver((o) => (o === item.id ? null : o))}
          onDrop={(e) => {
            e.preventDefault();
            drop(item.id);
          }}
          onDragEnd={() => {
            setDragging(null);
            setOver(null);
            setArmed(null);
          }}
          className={`group relative rounded-2xl transition ${dragging === item.id ? "opacity-40" : ""} ${
            over === item.id && dragging !== item.id ? "ring-2 ring-accent-blue" : ""
          }`}
        >
          <button
            type="button"
            aria-label="Drag to rearrange"
            title="Drag to rearrange"
            onMouseDown={() => setArmed(item.id)}
            onMouseUp={() => setArmed(null)}
            className="absolute right-2 top-2 z-10 flex h-6 w-6 cursor-grab items-center justify-center rounded-md text-text-tertiary opacity-0 transition hover:bg-black/5 focus:opacity-100 group-hover:opacity-100 active:cursor-grabbing"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
              {[8, 16].flatMap((x) => [6, 12, 18].map((y) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.6" />))}
            </svg>
          </button>
          {item.node}
        </div>
      ))}
    </div>
  );
}
